import React, { useCallback, useMemo, useReducer, useRef, useState } from "react";
import { Modal, notification, type MenuProps } from "antd";
import { SaveOutlined, UserOutlined } from "@ant-design/icons";
import type { ColDef, GridApi } from "ag-grid-community";
import type { IntlShape } from "react-intl";

import { getI18n } from "../../../../utils/Utils";
import { PersonalizeGridView } from "../../../info-portal/components/grid/GridUtil";

interface UseFreightGridToolbarOptions<TData> {
    columnDefs: ColDef<TData>[];
    exportWarningTitle?: string;
    intl: IntlShape;
    storageKey: string;
}

interface UseFreightGridToolbarResult {
    menuItems: MenuProps["items"];
    onExport: () => void;
    onGridReady: (params: { api: GridApi }) => void;
    onResetView: () => void;
}

export function useFreightGridToolbar<TData>({
    columnDefs,
    exportWarningTitle = "Export",
    intl,
    storageKey,
}: UseFreightGridToolbarOptions<TData>): UseFreightGridToolbarResult {
    const [gridApi, setGridApi] = useState<GridApi>(undefined);
    const [menuVersion, forceMenuRefresh] = useReducer((value: number) => value + 1, 0);
    const gridView = useRef<PersonalizeGridView>(undefined);

    const onSave = useCallback(() => {
        if (!gridView.current) {
            return;
        }

        gridView.current.savePersonalizedView();
        forceMenuRefresh();
        notification.info({ message: intl.formatMessage({ id: "report-result-preferences-saved" }) });
    }, [intl]);

    const onApplyPersonal = useCallback(() => {
        gridView.current?.applyPersonalizedView();
    }, []);

    const onResetView = useCallback(() => {
        gridApi?.setFilterModel(null);
        gridView.current?.reset();
    }, [gridApi]);

    const onGridReady = useCallback((params: { api: GridApi }) => {
        setGridApi(params.api);
        if (!gridView.current) {
            gridView.current = new PersonalizeGridView(storageKey, columnDefs);
        }
        gridView.current.initPersonalizedView(params.api);
    }, [columnDefs, storageKey]);

    const onExport = useCallback(() => {
        const columnIDs = gridApi
            ?.getColumnDefs()
            ?.filter((item) => !item["hide"])
            ?.map((item) => item["colId"]) ?? [];
        const selectedRows = gridApi?.getSelectedRows() ?? [];

        if (selectedRows.length && columnIDs.length) {
            gridApi.exportDataAsExcel({
                onlySelectedAllPages: true,
                columnKeys: columnIDs,
            });
            return;
        }

        Modal.warning({
            title: getI18n("Warning-message", exportWarningTitle, intl),
            content: getI18n(
                "Warning-export-message",
                "You have not selected any items to be exported. Please select an item first.",
                intl,
            ),
        });
    }, [exportWarningTitle, gridApi, intl]);

    const menuItems: MenuProps["items"] = useMemo(() => ([
        {
            key: `3-${menuVersion}`,
            disabled: !gridView.current?.hasPersonalizedView(),
            icon: <UserOutlined />,
            onClick: onApplyPersonal,
            label: (
                <span data-testid="info-portal-personal">
                    {intl.formatMessage({ id: "info-portal-personal", defaultMessage: "Personal" })}
                </span>
            ),
        },
        {
            type: "divider",
        },
        {
            key: `4-${menuVersion}`,
            icon: <SaveOutlined />,
            onClick: onSave,
            label: (
                <span data-testid="info-portal-save">
                    {intl.formatMessage({ id: "info-portal-save", defaultMessage: "Save" })}
                </span>
            ),
        },
    ]), [intl, menuVersion, onApplyPersonal, onSave]);

    return {
        menuItems,
        onExport,
        onGridReady,
        onResetView,
    };
}